import { Row, flexRender, getCoreRowModel, useReactTable, getPaginationRowModel, Cell } from "@tanstack/react-table";
import { ColumnType, TableProps, ThreeDotsTableTypes } from "./types/tableProps";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import './table.scss';
import { ReactComponent as ArrowDropUp } from "../../../icons/arrow_drop_up.svg";
import { ReactComponent as ArrowDropDown } from "../../../icons/arrow_drop_down.svg";
import { ReactComponent as MoreVert } from "../../../icons/more_vert.svg";
import { ReactComponent as ErrorIcon } from "../../../icons/error.svg";
import { iconStyle } from "../stylization";
import TableFilters from "./tableFilters/tableFilters";
import CheckboxField from "../../checkboxField/checkboxField";
import { CheckboxFieldProps } from "../../checkboxField/types/checkboxFieldProps";
import { TableContext } from "../../../contexts/tableContext";
import CheckboxCell from "./tableCheckbox/tableCheckbox";
import { mockUpData } from "./mockUpData.test";
import Spinner from "../../spinner/spinner";
import Navbar from "../navbar/navbar";
import { ClassNameNavbar, Option } from "../navbar/types/navbarProps";
import { ObjectState, ProcessMarker } from "../../../containers/centralView/taskListView/types/tasklListViewProps";
import { AdditionalIcon } from "../additionalIcon/additionalIcon";
import PermissionsContext from "../../../contexts/permissionsContext";
import { ObjectType } from "../../../contexts/types/card";
import { useClickOutside } from "../../../hooks/clickOutsideHook";
import { canAssignEditor, canAssignVerifier, canHaveEditorAssignedByAdmin, canHaveEditorAssignedByVerifier } from "../../../helpers/processHelper";
import AuthContext from "../../../contexts/authContext";
import { canBeEdited, canBeVerified, getCorrectFormPermissions, canBeCollectivelyEdited, checkAssigment } from "../../../helpers/permissionsHelper";
import { ObjectCategories } from "../../../containers/centralView/administrationView/AdministrationPermissionExtendedView/types/administrationsPermissionExtendedViewProps";
import Pagination from "../../pagination/pagination";
import { formatTableDate } from "../../../helpers/dateHelper";
import ViewTaskListContext from "../../../contexts/viewTaskListContext";
import TableDraggableRow from "./tableDraggableRow/tableDraggableRow";
import { CardDownloadMode } from "../../../services/registrationCardService/types/registrationCardServiceProps";
import ViewContext from "../../../contexts/viewContext";

const Table = (props: TableProps) => {
    const columns: any[] = [];
    const [dragging, setDragging] = useState<boolean>(false);
    const [filterView, setFilterView] = useState(false);
    const [openedByColumn, setOpenedByColumn] = useState('');
    const [checkedHeader, setCheckedHeader] = useState(false);
    const [showChecked, setShowChecked] = useState(false);
    const [menuOpened, setMenuOpened] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { viewTaskListTypes, viewTaskListType } = useContext(ViewTaskListContext);
    const { viewType, viewTypes } = useContext(ViewContext);
    useClickOutside(wrapperRef, () => {
        setMenuOpened(false);
    });

    var emptyRows: Row<any>[] = [];
    var Data = props.data && props.data.length > 0 ? props.data : emptyRows;
    if (props.mockUp) {
        Data = mockUpData();
    }
    const [data, setData] = useState(() => [...Data]);
    const { functionsPermissions, extendedUserPermissions } = useContext(PermissionsContext);
    const { onlineMode, userRoles, username } = useContext(AuthContext);
    const { checkboxesTable, setCheckboxesTable, filtersTableDb, filtersTableTaskList, sortConfigDb, sortConfigTaskList } = useContext(TableContext);
    const [shiftHeld, setShiftHeld] = useState(false);
    const refocusRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (props.refocus) {
            refocusRef.current?.focus();
        }
    }, [props.refocus]);

    useEffect(() => {
        if (props.shiftSelection) {
            window.addEventListener('keydown', downHandler);
            window.addEventListener('keyup', upHandler);
            return () => {
                window.removeEventListener('keydown', downHandler);
                window.removeEventListener('keyup', upHandler);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

    const downHandler = (e: any) => {
        if (e.shiftKey) {
            setShiftHeld(true);
        }
    }

    const upHandler = (e: any) => {
        if (e.key === "Shift") {
            setShiftHeld(false);
        }
    }

    useEffect(() => {
        if (!props.mockUp) {
            setData(props.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, props.mockUp]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: true,
        onPaginationChange: props?.setPagination,
        pageCount: props?.pageCount,
        state: {
            pagination: props?.pagination,
        }
    });

    const changeFilterVisible: React.MouseEventHandler<SVGSVGElement> = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        if (event.currentTarget.id === openedByColumn ||
            (event.currentTarget.id !== openedByColumn && !filterView)
            || openedByColumn === '' || !event.currentTarget.id) setFilterView(!filterView);
        setOpenedByColumn(event.currentTarget.id);
    }

    const onChangeCheckBoxColumnHeader = () => {
        let isChecked = !checkedHeader;
        toggleCheckViewAll(isChecked);
        setCheckedHeader(!checkedHeader);
    };

    const onChangeCheckBoxShowSelected = () => {
        setShowChecked(!showChecked);
    };

    const toggleCheckViewAll = (isCheck: boolean) => {
        if (showChecked === false && props.compareId !== undefined) {
            var checkedRows: Row<any>[] = [...checkboxesTable];
            var currentViewRows = [...table.getRowModel().rows];

            if (isCheck) {
                currentViewRows.forEach((c: Row<any>) => {
                    const containRow = _compareRows(checkedRows, c);
                    if (containRow === false) { checkedRows.push(c); }
                })
                setCheckboxesTable(checkedRows);
            } else {
                var filteredRows = checkedRows.filter(c => _compareRows(currentViewRows, c) === false);
                setCheckboxesTable(filteredRows);
            }
        } else {
            if (!isCheck) {
                setCheckboxesTable([]);
            }
        }
    }

    const _compareRows = (currRows: Row<any>[], compareRow: Row<any>) => {
        return currRows.some((ct: Row<any>) => {
            return props.compareId !== undefined ? props.compareId(ct, compareRow) : true;
        });
    }

    const selectAllOnClick = () => {
        toggleCheckViewAll(true);
        setCheckedHeader(true);
        setMenuOpened(false);
    };

    const CheckboxFieldPropsHeader: CheckboxFieldProps = {
        onChange: onChangeCheckBoxColumnHeader,
        inputChecked: checkedHeader,
        checkmarkStylization: {
            width: '16px',
            height: '16px',
            verticalAlign: 'middle'
        },
        style: {
            width: '100%',
            textAlign: 'center'
        },
        checkmarkSmall: true
    }

    const CheckboxFieldPropsShowChecked: CheckboxFieldProps = {
        onChange: onChangeCheckBoxShowSelected,
        inputChecked: showChecked,
        labelName: `Pokaż zaznaczone (${checkboxesTable.length})`,
        checkmarkStylization: {
            width: '16px',
            height: '16px',
            verticalAlign: 'middle'
        },
        style: {
            width: '100%',
            textAlign: 'center'
        },
        checkmarkSmall: true
    }

    const isSelected = (row: Row<any>) => {
        if (props.extraSelection && props.extraSelection.length > 0) {
            const extraSelected = hasRow(props.extraSelection, row);
            if (extraSelected) return true;
        }
        return hasRow(props.selection, row);
    }

    const checkIfMarkedForCommentFilter = (row: any) => {
        if (props.checkIfMarkedForCommentFilter) return props.checkIfMarkedForCommentFilter(row.original);
    }

    const isHighlight = (row: Row<any>) => {
        if (props.highlightFunc) {
            return props.highlightFunc(row);
        }
        return false;
    }

    const isEqual = (feature: any, row: Row<any>) => {
        if (props.rowCompare) {
            return props.rowCompare(feature, row);
        }
        return feature.ID === row.original.ID;
    }

    const handleTrClick = (e: any, row: Row<any>) => {
        if (e.target.id === 'checkboxes') {
            return;
        }
        if (props.setCommentsFilter) props.setCommentsFilter(row.original);
        if (props.onTrClick !== undefined && props.stopSelection) {
            props.onTrClick([row.original]);
            return;
        }
        if (props.onTrClick !== undefined && props.selection !== undefined) {
            const isSelected = hasRow(props.selection, row);
            if (isSelected) {
                //deselect
                if (e.ctrlKey) {
                    props.onTrClick(props.selection.filter(s => {
                        return !isEqual(s, row);
                    }));
                }
                else if (e.shiftKey && props.shiftSelection && props.selection.length > 0) {
                    handleShiftSelection(row);
                }
                else {
                    props.onTrClick([]);
                }
            } else {
                //select
                if (e.ctrlKey) {
                    props.onTrClick(props.selection.concat([row.original]));
                } 
                else if (e.shiftKey && props.shiftSelection && props.selection.length > 0) {
                    handleShiftSelection(row);
                }
                else {
                    props.onTrClick([row.original]);
                }
            }
        }
    }

    const handleShiftSelection = (row: Row<any>) => {
        var startingRowIndex: number = 0;       
        if (props.selection && props.onTrClick) {
            // Get starting point index
            if (props.selection.length > 1) {
                var startingRow = props.selection![props.selection!.length - 1];
                var selectedWithIndexes: any[] = [];
                props.selection.forEach(item => {
                    var idx = props.data.findIndex(elem => elem.attributes.ID === item.attributes.ID && elem.attributes.RODZAJ_OBIEKTU === item.attributes.RODZAJ_OBIEKTU);
                    selectedWithIndexes.push({ id: item.attributes.ID, index: idx, rodzajObiektu: item.attributes.RODZAJ_OBIEKTU });
                })
                selectedWithIndexes.sort((a: any, b: any) => a.index - b.index);
                startingRowIndex = selectedWithIndexes.find(item => item.id === startingRow.attributes.ID && item.rodzajObiektu === startingRow.attributes.RODZAJ_OBIEKTU).index;
            }
            else if (props.selection.length === 1) {
                startingRowIndex = props.data.findIndex(item => item.attributes.ID === props.selection![0].attributes.ID && item.attributes.RODZAJ_OBIEKTU === props.selection![0].attributes.RODZAJ_OBIEKTU);
            }
            
            // Get ending point index (row shift clicked)
            var endingRowIndex = props.data.findIndex(item => item.attributes.ID === row.original.attributes.ID && item.attributes.RODZAJ_OBIEKTU === row.original.attributes.RODZAJ_OBIEKTU);

            // Get selection direction 
            var isDown = startingRowIndex < endingRowIndex;

            // Set new selection to all rows in range if all selected rows exist
            if (startingRowIndex !== -1 && endingRowIndex !== -1) {
                var newSelection: __esri.Graphic[] = [];
                for (var i = isDown ? startingRowIndex : endingRowIndex; i <= (isDown ? endingRowIndex : startingRowIndex); i++) newSelection.push(props.data[i])    
                props.onTrClick(newSelection);
            }
            else {
                props.onTrClick(props.selection.concat([row.original]));
            }
        }
    }

    const hasRow = (rows: any[] | undefined, row: Row<any>) => {
        if (rows && rows.length > 0) {
            return rows.some((f: any) => {
                return isEqual(f, row);
            })
        }
        return false;
    }

    const reorderRow = (draggedRowIndex: number, targetRowIndex: number) => {
        if (draggedRowIndex === targetRowIndex || draggedRowIndex + 1 === targetRowIndex) {
            return;
        }
        let d = [...data];
        if (draggedRowIndex < targetRowIndex) {
            d.splice(targetRowIndex-1, 0, d.splice(draggedRowIndex, 1)[0] as any)
        } else {
            d.splice(targetRowIndex, 0, d.splice(draggedRowIndex, 1)[0] as any) 
        }
        setData(d)
        if (props.handleReorderRows) props.handleReorderRows(d)
    }

    const changeCheckboxes = (rows: Row<any>[]) => {
        setCheckboxesTable(rows);
    }

    if (props.firstColumnCheckbox) {
        columns.push(
            props.columnHelper?.display({
                header: () => { return (<CheckboxField {...CheckboxFieldPropsHeader}></CheckboxField>) },
                id: 'checkboxes',
                cell: (info) => {
                    return CheckboxCell(info.row, checkboxesTable, changeCheckboxes, props.compareId)
                }
            })
        )
    }

    if (props.startCellAction !== undefined) {
        columns.push(
            props.columnHelper?.display({
                header: () => { return (<div style={{ textAlign: 'left', width: '30px' }}></div>) },
                id: 'startactions',
                cell: info => { if (props.startCellAction !== undefined) { return props.startCellAction(info) } }
            })
        );
    }

    props.columnNames?.forEach((columnName: string) => {
        columns.push(
            props.columnHelper?.accessor(columnName, {
                id: columnName,
                cell: info => {
                    if (info.row.original.hasOwnProperty('attributes')) {
                        return info.row.original.attributes[columnName];
                    }
                    if (columnName === 'imagePreview') {
                        if (info.row.original.pliK_TYP && info.row.original.pliK_TYP === "application/pdf") {
                            return (
                                <p>pdf</p>
                            )
                        }
                        else {
                            return (
                                <div style={info.row.original.blob != null ? {position: "relative", marginTop: "-15px"} : {}}>
                                    <img src={`data:image/jpeg;base64,${info.row.original.blob}`} alt="preview" className={ info.row.original.blob != null ? "thumbnail" : "thumbnail-empty"}></img>
                                </div>
                            )
                        }
                    }
                    return info.getValue();
                },
                header: () => {
                    return (
                        <div>
                            <span style={{ margin: '1px', display: 'inline-block' }}>{props.columnAliases[columnName as keyof typeof props.columnAliases]}</span>
                            {showChecked === false && <div style={{ display: "flex" }}>
                                {props.tableFiltersVisible && ((columnName === openedByColumn || openedByColumn === '') && filterView ?
                                    <ArrowDropUp id={columnName} onClick={changeFilterVisible} style={iconStyle} /> :
                                    <ArrowDropDown id={columnName} onClick={changeFilterVisible} style={iconStyle} />)
                                }
                                {((viewType === viewTypes.DataBase ? filtersTableDb.find(item => item.filterColumnName === columnName) : filtersTableTaskList.find(item => item.filterColumnName === columnName)) || 
                                    (viewType === viewTypes.DataBase ? sortConfigDb.filterColumnName === columnName : sortConfigTaskList.filterColumnName === columnName)) &&
                                    <div className="filteredColumn"></div>
                                }
                            </div>}
                        </div>)
                },
                footer: props => props.column.id,

            }));
    })

    // Three dots menu options
    var checkStatus = (row: Row<any>, states: string[]) => {
        return states.includes(row.original.attributes.STAN_OBIEKTU_DOM);
    };

    const getAttributes = () => {
        return checkboxesTable[0].original.attributes ? checkboxesTable[0].original.attributes : checkboxesTable[0].original;
    }

    var checkForEdit = () => {
        if (checkboxesTable.length !== 1) return false;

        const waitingroom = getAttributes();
        if (waitingroom) {
            const assignedUser = waitingroom.PRZYPISANY_UZYTKOWNIK;
            const isAssigment = checkAssigment(username, assignedUser, userRoles);
            return isAssigment && canBeEdited(functionsPermissions, waitingroom, extendedUserPermissions, viewTaskListType === viewTaskListTypes.TaskListWaiting);
        }
        return false;
    }

    var checkForReturnCards = () => {
        return functionsPermissions.returnKRPO.active && checkboxesTable.every(item => extendedPermissionsCheck(item));
    }

    var checkForValidation = () => {
        if (checkboxesTable.length !== 1) return false;

        const waitingroom = getAttributes();
        if (waitingroom) {
            const objectStatus = waitingroom.STAN_OBIEKTU_DOM;
            const assignedUser = waitingroom.PRZYPISANY_UZYTKOWNIK;
            const isAssigment = checkAssigment(username, assignedUser, userRoles);
            return isAssigment && canBeVerified(functionsPermissions, objectStatus, viewTaskListType === viewTaskListTypes.TaskListWaiting);
        }
        return false;
    }

    var checkForAutomaticVerification = () => {
        if (!functionsPermissions.runAutomaticVerification.active) return false;
        if (!checkboxesTable.every(item => extendedPermissionsCheck(item))) return false;
        
        const isAssigment = isAssignForbidden(checkboxesTable) === false;

        if (viewTaskListType === viewTaskListTypes.TaskListWaiting) {
            let states = [ObjectState.DownloadedForEdition, ObjectState.DownloadedForVerification, ObjectState.AnalysisDecision, ObjectState.AnalysisError, 
                ObjectState.AnalysisSuccess, ObjectState.AnalysisInProgress, ObjectState.Uploaded];
            return isAssigment && isForbidden(checkboxesTable, states, true) === false;
        }
        return isAssigment && checkboxesTable.length > 0;
    }

    var checkForCardDownload = () => {
        let states = [ObjectState.ToBeVerified, ObjectState.Assigned, ObjectState.ToBeCorrected, ObjectState.WaitingRoomEdition, ObjectState.WaitingRoomEditionVerification];
        var isNotForbidden = isForbidden(checkboxesTable, states, false) === false;
        
        var hasEdit = checkForDownloadMode(true);
        var hasVerification = checkForDownloadMode(false);
        var isAllowed = {
            allowed: false,
            downloadMode: undefined
        };
        
        if (hasEdit) {
            isAllowed.allowed = functionsPermissions.downloadKRPOToEdit.active;
            if (!isAllowed.allowed) return isAllowed;
        }

        if (hasVerification) {
            isAllowed.allowed = functionsPermissions.downloadKRForVerification.active;
            if (!isAllowed.allowed) return isAllowed;
        } 

        var downloadMode = getDownloadMode(hasEdit, hasVerification);
        if (downloadMode === undefined) return {
                allowed: false,
                downloadMode: undefined
            };
        var isAllowedExtPerms = checkExtendedPermsForDownload(downloadMode);

        return {
            allowed: isNotForbidden && isAllowedExtPerms && isAllowed.allowed,
            downloadMode: downloadMode
        };
    }

    const checkExtendedPermsForDownload = (downloadMode: CardDownloadMode) => {
        if (downloadMode === CardDownloadMode.Edit) return checkboxesTable.every(item => extendedPermissionsCheck(item));
        else if (downloadMode === CardDownloadMode.Verification) return true;
        else if (downloadMode === CardDownloadMode.EditAndVerification) {
            var editStates: ObjectState[] = [ObjectState.Assigned, ObjectState.ToBeCorrected, ObjectState.WaitingRoomEdition];
            var checkboxesForEdit = checkboxesTable.filter(item => editStates.includes(item.original.attributes.STAN_OBIEKTU_DOM));
            return checkboxesForEdit.every(item => extendedPermissionsCheck(item));
        }
    }

    const checkForDownloadMode = (checkEdit: boolean) => {
        var editStates: ObjectState[] = [ObjectState.Assigned, ObjectState.ToBeCorrected, ObjectState.WaitingRoomEdition];
        var verificationStates: ObjectState[] = [ObjectState.ToBeVerified, ObjectState.WaitingRoomEditionVerification];
        return checkboxesTable.some(checkbox => {
            var checkboxed = checkbox
            if (!checkboxed.original.attributes) {
                checkboxed.original.attributes = checkboxed.original;
            }
            return checkEdit ? editStates.includes(checkboxed.original.attributes.STAN_OBIEKTU_DOM) : verificationStates.includes(checkboxed.original.attributes.STAN_OBIEKTU_DOM);
        });
    }

    const getDownloadMode = (isEdit: boolean, isVerification: boolean) => {
        if (isEdit && isVerification) return CardDownloadMode.EditAndVerification;
        else if (isEdit) return CardDownloadMode.Edit;
        else if (isVerification) return CardDownloadMode.Verification;
    }

    var checkForCollectiveEdit = () => {
        if (!functionsPermissions.editCollectivelyKR.active) return false;
        let firstObjectType: any;
        if (checkboxesTable[0].original.attributes) {
            firstObjectType = checkboxesTable[0].original.attributes.RODZAJ_OBIEKTU;
        }
        else {
            firstObjectType = checkboxesTable[0].original.RODZAJ_OBIEKTU;
        }

        const isAllowed = checkboxesTable.every((checkbox) => {
            var checkboxed = checkbox
            if (!checkboxed.original.attributes) {
                checkboxed.original.attributes = checkboxed.original;
            }
            const cardPattern = checkboxed.original.attributes.WZOR;
            const objectType = checkboxed.original.attributes.RODZAJ_OBIEKTU;
            const objectStatus = checkboxed.original.attributes.STAN_OBIEKTU_DOM;
            const teryts = checkboxed.original.attributes.TERYT;
            const hasGeom = checkboxed.original.attributes.SHAPE_YES_NO === 1;
            if (firstObjectType !== objectType) {
                return false;
            }
            const assignedUser = checkboxed.original.attributes.PRZYPISANY_UZYTKOWNIK;
            const isAssigment = checkAssigment(username, assignedUser, userRoles);
            return isAssigment && canBeCollectivelyEdited(cardPattern, objectStatus, objectType, extendedUserPermissions, teryts, hasGeom, viewTaskListType === viewTaskListTypes.TaskListWaiting);
        });
        return isAllowed;
    }

    var checkForCancelOAndK = () => {
        if (!functionsPermissions?.cancelEditTaskProcessOAndK.active) return false;
        return checkboxesTable.every(item => extendedPermissionsCheck(item));
    }

    var checkForCancelAAndN = () => {
        if (!functionsPermissions?.cancelProcessAAndN.active) return false;
        return onlineMode;
    }

    const extendedPermissionsCheck = (row: any) => {
        var checkboxed = row
        if (!checkboxed.original.attributes) {
            checkboxed.original.attributes = checkboxed.original;
        }
        
        const objectType: ObjectType = checkboxed.original.attributes.RODZAJ_OBIEKTU;
        const teryts: string = checkboxed.original.attributes.TERYT;

        var isAllowed = false;

        if (extendedUserPermissions.userObjectCategories?.length! > 0) {
            if (objectType === ObjectType.KRO) isAllowed = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide)!;
            if (objectType === ObjectType.KRTZ) isAllowed = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea)!;
            if (objectType === ObjectType.POO) isAllowed = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Protocol)! && extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide)!;
            if (objectType === ObjectType.POTZ) isAllowed = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Protocol)! && extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea)!;

            if (!isAllowed) return false;
        }
        if (teryts && teryts !== null && (extendedUserPermissions.userGminy?.length! > 0 || extendedUserPermissions.userPowiaty?.length! > 0)) {
            var terytList = teryts.split(', ');
            isAllowed = terytList.some(item =>
                extendedUserPermissions.userGminy?.includes(item) || extendedUserPermissions.userPowiaty?.includes(item.substring(0, 4)));
        }
        else isAllowed = true;

        return isAllowed;
    }

    var checkForDeleteKR = () => {
        if (!functionsPermissions?.deleteKRAndRelativeObjects.active) return false;
        return onlineMode;
    }

    var checkForMarkAsComplete = (row: Row<any>) => {
        const waitingroom = row.original.attributes;
        const objectType = waitingroom.RODZAJ_OBIEKTU;
        const assignedUser = waitingroom.PRZYPISANY_UZYTKOWNIK;
        const isAssigment = checkAssigment(username, assignedUser, userRoles);

        if (isAssigment && canBeEdited(functionsPermissions, waitingroom, extendedUserPermissions, viewTaskListType === viewTaskListTypes.TaskListWaiting)) {
            if (objectType === ObjectType.KRO || objectType === ObjectType.KRTZ) {
                return checkStatus(row, [ObjectState.WaitingRoomEdition, ObjectState.DownloadedForEdition])
            }
        }
        return false;
    }

    var checkForMarkAsToBeCorrected = (row: Row<any>) => {
        return checkForMarkAsAcceptedKR(row);
    }

    var checkForMarkAsAcceptedKR = (row: Row<any>) => {
        const objectType = row.original.attributes.RODZAJ_OBIEKTU;
        const objectStatus = row.original.attributes.STAN_OBIEKTU_DOM;

        if (viewTaskListType === viewTaskListTypes.TaskListWaiting && objectStatus === ObjectState.DownloadedForEdition) return false;
        if (objectType === ObjectType.KRO || objectType === ObjectType.KRTZ) {
            if (viewTaskListType === viewTaskListTypes.TaskListWaiting && objectStatus === ObjectState.DownloadedForVerification) return false;
            return checkStatus(row, [ObjectState.WaitingRoomEditionVerification, ObjectState.DownloadedForVerification])
        }
        return false;
    }

    var checkForMarkAsAcceptedPO = (row: Row<any>) => {
        const waitingroom = row.original.attributes;
        const objectType = waitingroom.RODZAJ_OBIEKTU;
        const assignedUser = waitingroom.PRZYPISANY_UZYTKOWNIK;
        const isAssigment = checkAssigment(username, assignedUser, userRoles);

        if (isAssigment && canBeEdited(functionsPermissions, waitingroom, extendedUserPermissions)) {
            if (objectType === ObjectType.POO || objectType === ObjectType.POTZ) {
                return checkStatus(row, [ObjectState.WaitingRoomEdition, ObjectState.DownloadedForEdition])
            }
        }
        return false;
    }

    var checkForMarkAsNoGeometry = (row: Row<any>) => {
        return checkForMarkAsAcceptedKR(row);
    }

    const addMarkStatusOption = (isPermitted: boolean, title: string, status: ObjectState, objType: string, callback: any) => {
        if (isPermitted && checkboxesTable.every(callback)) {
            navbarOptions.push({
                name: `Oznacz "${title}"`, onClick: () => {
                    if (props.threeDotsProps?.markStatus !== undefined) {
                        props.threeDotsProps?.markStatus(status, objType);
                    }
                    setMenuOpened(false);
                }
            });
        }

    }

    var checkAssignEditor = (row: Row<any>) => {
        return canAssignEditor(functionsPermissions.assignEditor.active, userRoles, row.original.attributes.STAN_OBIEKTU_DOM, viewTaskListType === viewTaskListTypes.TaskListWaiting);
    }

    const addAssignEditor = (isPermitted: boolean, callback: any, objType: string[]) => {
        var objectType = checkboxesTable.every((item) => { return objType.includes(item.original.attributes.RODZAJ_OBIEKTU) })
        var actionByVerifier = checkboxesTable.every((item) => { return canHaveEditorAssignedByVerifier(item.original.attributes.STAN_OBIEKTU_DOM) });
        var singleAction = actionByVerifier || checkboxesTable.every((item) => { return canHaveEditorAssignedByAdmin(item.original.attributes.STAN_OBIEKTU_DOM) });

        if (objectType && singleAction && isPermitted && checkboxesTable.every(callback)) {
            navbarOptions.push({
                name: "Przypisz Edytora", onClick: () => {
                    if (props.threeDotsProps?.assignEditorOnClick !== undefined) {
                        props.threeDotsProps?.assignEditorOnClick(actionByVerifier);
                    }
                    setMenuOpened(false);
                }
            });
        }
    }

    const checkForAddProtocol = () => {
        if (checkboxesTable.length !== 1) return false;

        const objType = checkboxesTable[0].original.attributes.RODZAJ_OBIEKTU
        if (objType === ObjectType.POO || objType === ObjectType.POTZ) return false;

        const isLandslide = checkboxesTable[0].original.attributes.RODZAJ_OBIEKTU === ObjectType.KRO;
        var extPerms = true;
        if (extendedUserPermissions.userObjectCategories && extendedUserPermissions.userObjectCategories?.length! > 0) {
            extPerms = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Protocol) &&
                (isLandslide ? extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide) :
                    extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea))
        }

        if (functionsPermissions.addPO.active && extPerms) {
            navbarOptions.push({
                name: 'Dodaj protokół', onClick: () => {
                    if (props.threeDotsProps?.addProtocolOnClick !== undefined) { props.threeDotsProps?.addProtocolOnClick() };
                    setMenuOpened(false);
                }
            });
        }
    }

    const checkForUpdateRec = () => {
        var extPerms = checkboxesTable.every(item => checkForUpdateRecExtPerms(item));
        var krChecked = checkboxesTable.some(item => item.original.attributes.RODZAJ_OBIEKTU === ObjectType.KRO || item.original.attributes.RODZAJ_OBIEKTU === ObjectType.KRTZ);
        var poChecked = checkboxesTable.some(item => item.original.attributes.RODZAJ_OBIEKTU === ObjectType.POO || item.original.attributes.RODZAJ_OBIEKTU === ObjectType.POTZ);
        var waitingRoomCheck = checkboxesTable.every(item => item.original.attributes.POCZEKALNIA === 0);

        if (extPerms && checkForUpdateRecPermissions(krChecked, poChecked) && waitingRoomCheck) {
            navbarOptions.push({
                name: 'Aktualizuj', onClick: () => {
                    if (props.threeDotsProps?.updateRecOnClick !== undefined) { props.threeDotsProps?.updateRecOnClick() };
                    setMenuOpened(false);
                }
            });
        }
    }

    const checkForUpdateRecExtPerms = (row: any) => {
        if(!row.original.attributes){
            row.original.attributes = row.original;
        }
        let objectType = row.original.attributes.RODZAJ_OBIEKTU;
        let teryts = row.original.attributes.TERYT;
        let isProtocol = objectType === ObjectType.POO || objectType === ObjectType.POTZ;

        return getCorrectFormPermissions(isProtocol, objectType, teryts, functionsPermissions, extendedUserPermissions);
    }

    const checkForUpdateRecPermissions = (krChecked: boolean, poChecked: boolean) => {
        var isAllowed: boolean = false;
        if (krChecked) {
            isAllowed = functionsPermissions.processK.active;
            if (!isAllowed) return false;
        }
        if (poChecked) isAllowed = functionsPermissions.correctPO.active;

        return isAllowed;
    }

    var checkAssignVerifier = (row: Row<any>) => {
        return canAssignVerifier(functionsPermissions.assignVerifier.active, extendedUserPermissions, userRoles, row.original.attributes.RODZAJ_OBIEKTU,
            row.original.attributes.STAN_OBIEKTU_DOM, row.original.attributes.TERYT, viewTaskListType === viewTaskListTypes.TaskListWaiting, row.original.attributes.ZNACZNIK_PROCESU_DOM);
    }

    const addAssignVerifier = (isPermitted: boolean, callback: any, objType: string[]) => {
        var objectType = checkboxesTable.every((item) => { return objType.includes(item.original.attributes.RODZAJ_OBIEKTU) });
        if (objectType && isPermitted && checkboxesTable.every(callback)) {
            navbarOptions.push({
                name: "Przypisz Weryfikatora", onClick: () => {
                    if (props.threeDotsProps?.assignVerifierOnClick !== undefined) {
                        props.threeDotsProps?.assignVerifierOnClick();
                    }
                    setMenuOpened(false);
                }
            });
        }
    }

    const addAutomaticVerification = () => {
        if (checkForAutomaticVerification()) {
            navbarOptions.push({
                name: 'Waliduj', onClick: () => {
                    if (checkForAutomaticVerification() && props.threeDotsProps?.automaticVerificationClick) { props.threeDotsProps?.automaticVerificationClick() };
                }
            });
        }
    }

    const addCardDownload = () => {
        if (checkForCardDownload().allowed) {
            navbarOptions.push({
                name: 'Pobierz kartę na komputer', onClick: () => {
                    var check = checkForCardDownload();
                    if (check.allowed && props.threeDotsProps?.downloadCardClick) { props.threeDotsProps?.downloadCardClick(check.downloadMode) };
                }
            });
        }
    }

    const addReturnCard = () => {
        navbarOptions.push({
            name: `Zwróć karty do poczekalni`, onClick: () => {
                if (props.threeDotsProps?.ReturnCardClick !== undefined) {
                    props.threeDotsProps?.ReturnCardClick();
                }
                setMenuOpened(false);
            }
        });
    }

    const isForbidden = (rows: Row<any>[], states: string[], include: boolean) => {
        return rows.length === 0 || rows.some((checkbox) => {
            let state = getState(checkbox);
            let process = getProcess(checkbox);
            return process === ProcessMarker.U || states.includes(state) === include;
        });
    }

    const isAssignForbidden = (rows: Row<any>[]) => {
        return rows.length === 0 || rows.some((checkbox) => {
            const assignedUser = getAttribute(checkbox, "PRZYPISANY_UZYTKOWNIK");
            const isAssigment = checkAssigment(username, assignedUser, userRoles);
            return !isAssigment;
        });
    }

    const getState = (row: Row<any>) => {
        return getAttribute(row, "STAN_OBIEKTU_DOM");
    }

    const getProcess = (row: Row<any>) => {
        return getAttribute(row, "ZNACZNIK_PROCESU_DOM");
    }

    const getAttribute = (row: Row<any>, name: string) => {
        return row.original.attributes ? row.original.attributes[name] : row.original[name];
    }

    const navbarOptions: Option[] = showChecked ? [] : [ { name: 'Zaznacz wszystko', onClick: selectAllOnClick } ];
    if (checkboxesTable.length > 0) {
        navbarOptions.push({
            name: 'Odznacz wszystko', onClick: () => { setCheckboxesTable([]); }
        });
        switch (props.threeDotsProps?.table) {
            case ThreeDotsTableTypes.TaskListLocal:
                if (checkForEdit()) {
                    navbarOptions.push({
                        name: 'Edytuj', onClick: () => {
                            if (checkForEdit()) { props.threeDotsProps?.editOnClick() };
                        }
                    });
                }
                if (checkForCollectiveEdit()) {
                    navbarOptions.push({
                        name: 'Edytuj zbiorczo', onClick: () => {
                            if (checkForCollectiveEdit()) { props.threeDotsProps?.collectiveEditOnClick() };
                        }
                    });
                }
                if (checkForValidation()) {
                    navbarOptions.push({
                        name: 'Weryfikuj', onClick: () => {
                            if (checkForValidation() && props.threeDotsProps?.validateOnClick) { props.threeDotsProps?.validateOnClick() };
                        }
                    });
                }
                addAutomaticVerification();
                if (checkForReturnCards()) {
                    addReturnCard();
                }
                break;
            case ThreeDotsTableTypes.TaskListWaiting:
                if (checkForEdit()) {
                    navbarOptions.push({
                        name: 'Edytuj', onClick: () => {
                            if (checkForEdit()) { props.threeDotsProps?.editOnClick() };
                        }
                    });
                }
                if (checkForCollectiveEdit()) {
                    navbarOptions.push({
                        name: 'Edytuj zbiorczo', onClick: () => {
                            if (checkForCollectiveEdit()) { props.threeDotsProps?.collectiveEditOnClick() };
                        }
                    });
                }
                if (checkForValidation()) {
                    navbarOptions.push({
                        name: 'Weryfikuj', onClick: () => {
                            if (checkForValidation()) { props.threeDotsProps?.validateOnClick() };
                        }
                    });
                }

                addAssignEditor(functionsPermissions?.assignEditor.active, checkAssignEditor, [ObjectType.KRO, ObjectType.KRTZ]);
                addAssignVerifier(functionsPermissions?.assignVerifier.active, checkAssignVerifier, [ObjectType.KRO, ObjectType.KRTZ]);
                addMarkStatusOption(functionsPermissions?.markKRComplete.active, "Kompletna", ObjectState.Complete, "KR", checkForMarkAsComplete);
                addMarkStatusOption(functionsPermissions?.markKRToBeCorrected.active, "Do poprawy", ObjectState.ToBeCorrected, "KR", checkForMarkAsToBeCorrected);
                addMarkStatusOption(functionsPermissions?.markKRAccepted.active, "Zatwierdzona", ObjectState.Accepted, "KR", checkForMarkAsAcceptedKR);
                addMarkStatusOption(functionsPermissions?.markPOAccepted.active, "Zatwierdzona", ObjectState.Accepted, "PO", checkForMarkAsAcceptedPO);
                addMarkStatusOption(functionsPermissions?.markKRNoGeom.active, "Oczekuje na geometrię", ObjectState.NoGeometry, "KR", checkForMarkAsNoGeometry);
                addCardDownload();
                addAutomaticVerification();

                if (checkForCancelOAndK()) {
                    navbarOptions.push({
                        name: 'Anuluj zadanie edycji (o,k)', onClick: () => {
                            if (checkForCancelOAndK()) { props.threeDotsProps?.cancelOAndKOnClick() };
                        }
                    });
                }
                if (checkForCancelAAndN()) {
                    navbarOptions.push({
                        name: 'Anuluj zadanie (a,n)', onClick: () => {
                            if (checkForCancelAAndN()) { props.threeDotsProps?.cancelAAndNClick() };
                        }
                    });
                }
                break;

            case ThreeDotsTableTypes.DataBase:
                if (checkboxesTable.length > 0 && checkForDeleteKR()) {
                    navbarOptions.push({
                        name: 'Skasuj KR/PO', onClick: () => {
                            if (checkForDeleteKR()) { props.threeDotsProps?.deleteKROnClick() };
                        }
                    });
                }
                checkForUpdateRec();
                checkForAddProtocol();
                break;

            case ThreeDotsTableTypes.DictionaryProposals:
                navbarOptions.push({
                    name: 'Zatwierdź', onClick: () => {
                        props.threeDotsProps?.approveDictProposalOnClick();
                        setMenuOpened(false);
                    }
                });
                navbarOptions.push({
                    name: 'Odrzuć', onClick: () => {
                        props.threeDotsProps?.rejectDictProposalOnClick();
                        setMenuOpened(false);
                    }
                });
        }

    }

    const moreVertToggle = () => {
        setMenuOpened(!menuOpened);
    }

    columns.push(
        props.columnHelper?.display({
            header: () => {
                return (<div style={{ textAlign: 'right', paddingRight: '20px' }}>{props.threeDots && <div onClick={moreVertToggle}><MoreVert style={iconStyle} width={4} height={16} />
                </div>}</div>)
            },
            id: 'actions',
            cell: info => { if (props.cellActions !== undefined) { return props.cellActions(info) } }
        })
    );

    const getFoldoutRows = (row: any) => {
        return (
            <>
                {props.foldoutRows && props.rowsToFoldOut?.includes(props.foldoutParamToCheck && props.foldoutParamToCheck(row.original)) && <tr><td className="foldoutContainer" colSpan={100}>{props.foldoutContent && props.foldoutContent(row.original)}</td></tr>}
                {props.errorRows && props.errorRows?.includes(props.foldoutParamToCheck && props.foldoutParamToCheck(row.original)) && <tr><td className="foldoutErrorContainer" colSpan={100}><p>{AdditionalIcon(ErrorIcon)}{props.errorRowsMsg}</p></td></tr>}
            </>
        )
    }

    const toDisplayValue = (cell: Cell<any, unknown>) => {
        if (!props.columnConfig) {
            return flexRender(cell.column.columnDef.cell, cell.getContext())
        }
        const col = props.columnConfig.find(c => c.name === cell.column?.id);
        if (col !== undefined) {
            const value = cell.row.original.attributes ? cell.row.original.attributes[col.name] : cell.row.original[col.name];
            if (col.type === ColumnType.Date) {
                return toDateValue(value as number);
            }
        }
        return flexRender(cell.column.columnDef.cell, cell.getContext())
    }

    const toDateValue = (value?: number) => {
        if (value === null || value === undefined) return "";
        return formatTableDate(new Date(value));
    }

    const getRowId = (row: any) => {
        if(!row.original.attributes){
            return row.original.GLOBALID ? row.original.GLOBALID : row.id;
        }
        return row.original.attributes?.GLOBALID ? row.original.attributes.GLOBALID : row.id;
    }

    const renderRow = () => {
        let rows = showChecked ? checkboxesTable : table.getRowModel().rows;

        if (props.draggableRows) {
            return rows.map(row => (
                <Fragment key={'fTr' + row.id}>
                    <TableDraggableRow
                        row={row}
                        dragging={dragging}
                        setDragging={setDragging}
                        reorderRow={reorderRow}
                        onTrClick={handleTrClick}
                        hasSelectClass={checkIfMarkedForCommentFilter(row)} />
                    {getFoldoutRows(row)}
                </Fragment>
            ))
        }
        return rows.map(row => {
            let trClassName = (isSelected(row) || checkIfMarkedForCommentFilter(row)) ? "table-RowSelected" : "";
            if (isHighlight(row)) trClassName += " table-RowHighlight";
            return <Fragment key={'fTr' + getRowId(row)}>
                <tr onClick={(e) => { handleTrClick(e, row) }} className={`${trClassName} ${props.isTrClickable ? "rowClickable" : ""}`}>
                    {row.getVisibleCells().map(cell => (
                        <td key={'td' + cell.id} className={shiftHeld ? "unselectable" : ""} style={cell.id === "actions" ? { textAlign: "right" } : undefined}>
                            {toDisplayValue(cell)}
                        </td>
                    ))}
                </tr>
                {getFoldoutRows(row)}
            </Fragment>
        })
    }

    return (
        <>
            <div className="tableView" style={ props.narrowTableForCommentBtns ? {width: "calc(100% - 54px)"} : {}}
                ref={refocusRef} tabIndex={props.refocus ? 0 : -1} onBlur={() => { if (props.setRefocusTable) props.setRefocusTable(false) }}>
                {props.firstColumnCheckbox && <div className="tableView-selection">
                    <CheckboxField {...CheckboxFieldPropsShowChecked} style={{width: "auto"}}/>
                </div>}
                <div style={{ position: 'relative' }}>
                    {!props.loading && props.threeDots && <div ref={wrapperRef}><Navbar rolledOut={menuOpened} options={navbarOptions} itemClassName={props.threeDotsProps?.menuItemClass} style={props.threeDotsProps?.menuStyle} addClass={ClassNameNavbar.threeDots} /></div>}
                </div>
                <div className={props.scrollable ? 'tableView-scroll' : ''}>
                <table cellSpacing={0} width="100%" className={props.className}>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup, idx) => (
                            <tr key={idx}>
                                {headerGroup.headers.map(header => {
                                    let style: any = { minWidth: "30px" };
                                    const cellMinWidth = props.cellWidth && props.cellWidth[header.column.id];
                                    if (cellMinWidth) { style.minWidth = cellMinWidth };
                                    if (header.column.id === 'checkboxes' || header.column.id === 'startactions' || header.column.id === 'actions') {
                                        style = { width: "30px" };
                                    }
                                    return <th key={header.id} style={style} className={props.tableFiltersVisible && filterView && header.id === openedByColumn ? 'activeColumn' : undefined}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </th>
                                })}
                            </tr>
                        ))}
                        {showChecked === false && props.tableFiltersVisible &&
                            <tr key="filterRow">
                                <th
                                    colSpan={columns.length}
                                    className={`filterTable ${filterView ? '' : 'filterTableClosed'}`}
                                    style={{
                                        height: filterView ? '68px' : '0px'
                                    }}>
                                    <TableFilters openedByColumn={openedByColumn} isOpened={filterView} closeFilters={changeFilterVisible} columnConfig={props.columnConfig} isDb={viewType === viewTypes.DataBase}/>
                                </th>
                            </tr>}
                    </thead>
                    <tbody>
                        {renderRow()}
                        {props.tableError &&
                            <tr key="trErr">
                                <td className="foldoutErrorContainer" colSpan={100}>
                                    <p>{AdditionalIcon(ErrorIcon)}{props.tableErrorMsg}</p>
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
                </div>
                {showChecked === false && props.isPagination && <Pagination table={table}
                    allCount={props.allCount}
                    pageCount={props.pageCount}
                    setPageCount={props.setPageCount}
                    pagination={props.pagination}
                    setPagination={props.setPagination}
                    initialPageSize={props.pagination?.pageSize}
                    onChange={props.onPaginationChange}
                />}
                {props.loading && <Spinner isMist={true} />}
            </div>
        </>
    )
}

export default Table;